import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

export enum StatusGroup {
  Lead = 1,
  Client = 2
}

export enum StatusType {
  Standard = 0,
  Initial = 1,
  Success = 2,
  Cancelled = 3,
  InProgress = 4
}

export interface StatusDto {
  id: number;
  name: string;
  description?: string;
  color: string;
  group: StatusGroup;
  groupName: string;
  statusType: StatusType;
  statusTypeName: string;
  isDefault: boolean;
  countInPipeline: boolean;
  countAsConversion: boolean;
  sortOrder: number;
  nextStatuses: number[];
  nextStatusesDetails: StatusDto[];
  autoAlertDays?: number;
  alertContent?: string;
  createReminder: boolean;
  isActive: boolean;
  isSystemDefault: boolean;
  expectedDurationDays?: number;
  maxDurationDays?: number;
  isHidden: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStatusDto {
  name: string;
  description?: string;
  color: string;
  group: StatusGroup;
  statusType: StatusType;
  isDefault: boolean;
  countInPipeline: boolean;
  countAsConversion: boolean;
  sortOrder: number;
  nextStatuses: number[];
  autoAlertDays?: number;
  alertContent?: string;
  createReminder: boolean;
  isActive: boolean;
  expectedDurationDays?: number;
  maxDurationDays?: number;
  isHidden: boolean;
}

export interface UpdateStatusDto {
  name: string;
  description?: string;
  color: string;
  group: StatusGroup;
  statusType: StatusType;
  isDefault: boolean;
  countInPipeline: boolean;
  countAsConversion: boolean;
  sortOrder: number;
  nextStatuses: number[];
  autoAlertDays?: number;
  alertContent?: string;
  createReminder: boolean;
  isActive: boolean;
  expectedDurationDays?: number;
  maxDurationDays?: number;
  isHidden: boolean;
}

export interface StatusImpactDto {
  statusId: number;
  statusName: string;
  usageCount: number;
  isSystemDefault: boolean;
  isOnlyDefault: boolean;
  isOnlyConversion: boolean;
  isOnlyInitial: boolean;
  affectsPipeline: boolean;
  affectsReminders: boolean;
  canBeDeleted: boolean;
  warnings: string[];
  errors: string[];
}

// API functions
export const statusApi = {
  // Get all statuses
  getAll: async (group?: StatusGroup, includeHidden = false): Promise<StatusDto[]> => {
    const response = await axios.get(`${API_BASE_URL}/Status`, {
      params: { group, includeHidden }
    });
    return response.data;
  },

  // Get statuses by group
  getByGroup: async (group: StatusGroup, includeHidden = false): Promise<StatusDto[]> => {
    const response = await axios.get(`${API_BASE_URL}/Status/group/${group}`, {
      params: { includeHidden }
    });
    return response.data;
  },

  // Get status by ID
  getById: async (id: number): Promise<StatusDto> => {
    const response = await axios.get(`${API_BASE_URL}/Status/${id}`);
    return response.data;
  },

  // Create status
  create: async (status: CreateStatusDto): Promise<StatusDto> => {
    const response = await axios.post(`${API_BASE_URL}/Status`, status);
    return response.data;
  },

  // Update status
  update: async (id: number, status: UpdateStatusDto): Promise<StatusDto> => {
    const response = await axios.put(`${API_BASE_URL}/Status/${id}`, status);
    return response.data;
  },

  // Delete status
  delete: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/Status/${id}`);
  },

  // Reorder statuses
  reorder: async (group: StatusGroup, orderMap: { [key: number]: number }): Promise<void> => {
    await axios.put(`${API_BASE_URL}/Status/reorder/${group}`, orderMap);
  },

  // Check if status can be deleted
  canDelete: async (id: number): Promise<{ canDelete: boolean; usageCount: number; message?: string }> => {
    const response = await axios.get(`${API_BASE_URL}/Status/${id}/can-delete`);
    return response.data;
  },

  // Get status impact - what will be affected by deletion
  getImpact: async (id: number): Promise<StatusImpactDto> => {
    const response = await axios.get(`${API_BASE_URL}/Status/${id}/impact`);
    return response.data;
  }
};

// טיפול אחיד בשגיאות API — מקור אמת מרכזי
export { handleApiError } from '../utils/errorHandler';

// Helper functions
export const getStatusTypeName = (type: StatusType): string => {
  switch (type) {
    case StatusType.Initial:
      return 'התחלתי';
    case StatusType.Success:
      return 'הצלחה';
    case StatusType.Cancelled:
      return 'בוטל';
    case StatusType.InProgress:
      return 'בתהליך';
    default:
      return 'רגיל';
  }
};

export const getStatusGroupName = (group: StatusGroup): string => {
  return group === StatusGroup.Lead ? 'לידים' : 'לקוחות';
};

// Default colors for statuses
export const STATUS_COLORS = [
  '#008EA2', // Monday.com blue
  '#16a34a', // Success green
  '#d97706', // Orange
  '#dc2626', // Error red
  '#579bfc', // Light blue
  '#a25ddc', // Purple
  '#ef4444', // Light red
  '#ca8a04', // Yellow
  '#15803d', // Green
  '#7f77f1', // Violet
  '#b91c1c', // Dark red
];

export default statusApi;